import { useEffect } from "react";

import { GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { env } from "next-runtime-env";

import CreationFlow from "components/creationFlow/CreationFlow";
import { GenericError } from "components/errorBoundary/ErrorBoundary";
import { clearAll } from "db/museImages/museImages.dexie";
import { Bugsnag } from "lib/bugsnag";
import {
  useGetCustomisation,
  useGetProduct,
  useGetSchema,
  useSetCustomisation,
} from "queries/legacy/product-queries";
import { trackGenericErrorEvent } from "tracking/events/track-generic-error-event";
import { GetCanvasStaticProps } from "types/queries";
import { SSR_CONFIGURATION_ERROR } from "utils/legacy/error-utils";
import { getLocaleFromMarketplace } from "utils/legacy/prismic-utils";
import {
  getCanvasStaticPaths,
  getCanvasStaticPropsFactory,
} from "utils/legacy/server-utils";

export default function Start({
  ssrError,
}: GetCanvasStaticProps<ReturnType<typeof getCanvasStaticPropsFactory>>) {
  const router = useRouter();

  const { data: product } = useGetProduct();
  const { error: schemaError, isLoading: schemaIsLoading } = useGetSchema();
  const { error: customisationError } = useGetCustomisation();

  const { mutate: setCustomisation } = useSetCustomisation();

  useEffect(() => {
    if (customisationError) {
      /**
       * If Creation Flow is visited without any "customisation",
       * set a best-guess locale rather than blowing up.
       */
      setCustomisation({
        data: {
          locale: getLocaleFromMarketplace(router.query.marketplace as string),
          product_slug: product?.museSlug,
        },
      });
    }
  }, [
    customisationError,
    router.query.marketplace,
    setCustomisation,
    product?.museSlug,
  ]);

  useEffect(() => {
    if (ssrError) {
      trackGenericErrorEvent(product, ssrError);
    }
  }, [ssrError, product]);

  useEffect(() => {
    if (schemaError) {
      trackGenericErrorEvent(product, schemaError?.message);
    }
  }, [schemaError, product]);

  // Clear muse-images cache when entering canvas from PDP previously handled on the PDP page itself, test it here
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.sessionStorage === "undefined"
    ) {
      return;
    }

    try {
      if (window.sessionStorage.getItem("clear_muse_cache")) {
        clearAll();
        window.sessionStorage.removeItem("clear_muse_cache");
      }
    } catch (error) {
      Bugsnag.notify(
        error instanceof Error
          ? error
          : new Error("Error clearing muse-images cache: " + error),
        (event) => {
          event.severity = "error";
          event.unhandled = false;
        }
      );
    }
  }, []);

  if (ssrError || schemaError || !product) {
    // Log error details to Bugsnag for easier debugging
    let error =
      schemaError || (!product ? new Error("Product not found") : undefined);

    if (!error && ssrError) {
      error = new Error(typeof ssrError === "string" ? ssrError : "SSR Error");
    }

    if (error) {
      Bugsnag.notify(error, (event) => {
        event.severity = "error";
        event.unhandled = false;

        event.addMetadata("/create/start - Error details:", {
          marketplace: router.query.marketplace,
          product: product?.museSlug,
        });
      });
    }
  }

  if (ssrError) {
    return <GenericError error={Error(ssrError)} fullPage />;
  }

  if (!product) {
    trackGenericErrorEvent(null, SSR_CONFIGURATION_ERROR.message);
    throw SSR_CONFIGURATION_ERROR;
  }

  if (schemaError) {
    return <GenericError error={schemaError} fullPage />;
  }

  if (schemaIsLoading) {
    // TODO: loading state / skeleton
    return null;
  }

  return (
    <>
      <Head>
        <title>
          {product.content.product.muse_product_name
            ? `${product.content.product.muse_product_name} | Wonderbly`
            : `Create | Wonderbly`}
        </title>
      </Head>

      <CreationFlow />
    </>
  );
}

export const getStaticPaths = getCanvasStaticPaths;
export const getStaticProps: GetStaticProps = async (context) => {
  const result = await getCanvasStaticPropsFactory("personalisation")(context);

  if ("notFound" in result) {
    return result;
  }

  // Redirect non-PDC books to website on prod
  if (!result.props.usesPdc && env("NEXT_PUBLIC_CANVAS_ENV") === "production") {
    return {
      redirect: {
        destination:
          (context.params!.marketplace === "us"
            ? `/${context.params!.marketplace}`
            : "") +
          "/personalized-products/" +
          context.params!.solidus_slug,
        permanent: false,
      },
    };
  }

  return result;
};
